import type { KitNote } from '../kit/kit';

/**
 * Lab Notes: who sees a note, who may write one privately, and what is unread.
 * Request of 4 October 2026, section 7.
 *
 * The server's LabNoteRules (Dentist.Domain/Ceramica/LabNoteRules.cs) is the
 * same rule and is the one that counts: it leaves a private note out of every
 * response to somebody it is not for. This copy lets the mock server answer the
 * same way and lets the screen offer only what the server will accept.
 */

/** Who sees the note that comes with an assignment. */
export const NOTE_VISIBILITIES = ['everyone', 'assignee'] as const;
export type NoteVisibility = (typeof NOTE_VISIBILITIES)[number];

export const LAB_NOTE_REFUSAL = {
  notAllowedPrivately:
    'You can reply privately only to somebody who wrote to you privately on this case.',
  nobodyToTell: 'That person is not in the system.',
  forwardOnlyManagement: 'Only management or a supervisor may forward a private note.',
  forwardOnlyPrivate: 'Only a private note on this case can be forwarded.',
  privateNeedsSomebody: 'A private note needs somebody to be for.',
} as const;

/** Management and supervisors read every note on a case, private ones included. */
export function seesEveryNote(caller: { permissions: readonly string[]; isSupervisor: boolean }): boolean {
  return caller.permissions.includes('admin') || caller.isSupervisor;
}

/**
 * Whether `callerId` sees a note. A public note is everybody's on the case. A
 * private one is its writer's, its reader's, and management's and supervisors'.
 * It follows the person and not the assignment, so a technician a case is
 * reassigned to does not inherit what was written privately to the one before.
 */
export function canSeeNote(
  note: { visibleToUserId: string | null; byUserId: string | null },
  callerId: string,
  seesAll: boolean,
): boolean {
  return (
    note.visibleToUserId === null ||
    seesAll ||
    note.byUserId === callerId ||
    note.visibleToUserId === callerId
  );
}

/**
 * Whether the caller may write privately. Management and supervisors may write
 * to anybody; anybody else only answers somebody who wrote to them privately.
 */
export function mayWritePrivately(seesAll: boolean, wroteToCallerPrivately: boolean): boolean {
  return seesAll || wroteToCallerPrivately;
}

/** Notes by somebody else written since the caller last read, the count on the button. */
export function unreadNotes(items: readonly KitNote[], lastReadAt: string | null, me: string): number {
  return items.filter(
    (note) => note.byUserId !== me && (lastReadAt === null || note.createdAt > lastReadAt),
  ).length;
}

/**
 * Who a reply goes to privately by default: the writer of the newest private
 * note addressed to the caller, or null when nobody wrote to them privately.
 * A reply to a private note is private unless they choose otherwise.
 */
export function privateReplyTo(
  items: readonly KitNote[],
  me: string,
): { id: string; name: string } | null {
  const newest = [...items]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .find((note) => note.visibleTo?.id === me && note.byUserId !== null && note.byUserId !== me);
  return newest?.byUserId === null || newest?.byUserId === undefined
    ? null
    : { id: newest.byUserId, name: newest.byUserName };
}

/**
 * Whether a row is an event the system recorded rather than a note somebody
 * wrote: it carries the system's own sentence, or it is of any kind but a plain
 * note (a flag, a rework, a hold). Lab Notes draws an event as a line across
 * the conversation and a note as a message. One meaning, here, since the
 * request of 5 October 2026 (third), section 4; the screen had a wider rule of
 * its own before.
 */
export function isEvent(note: Pick<KitNote, 'event' | 'kind'>): boolean {
  return note.event !== null || note.kind !== 'note';
}
